import Bugsnag from '@bugsnag/js';

/**
 * Diagnostics for chunk load failures.
 *
 * A failed dynamic import surfaces in the browser as the generic
 * "Failed to fetch dynamically imported module", which says nothing about why
 * (edge 5xx, cached error, CORS, network, stale tab). These helpers capture the
 * surrounding context so a failure can be explained after the fact.
 *
 * Everything here is best-effort: browser APIs are accessed defensively and no
 * function may throw or get in the way of the recovery path that calls it.
 */

const LAST_FAILURE_KEY = 'chunk-last-failure';
const REPORT_COUNTS_KEY = 'chunk-reports';
const LOG_LABEL = '[chunk-diagnostics]';
const ENTRY_SCRIPT_SELECTOR = 'script[type="module"][src*="/assets/index."]';
// Captures the content hash of the entry bundle, e.g. index.DGIDG_pm.js.
const ENTRY_URL_PATTERN = /\/assets\/index\.([\w-]+)\.js/;
const MAX_MESSAGE_LENGTH = 300;
// Two reports per chunk per tab: the first failure, and the one after the
// recovery reload (which doubles as a "still failing?" re-probe).
const MAX_REPORTS_PER_CHUNK = 2;
const PROBE_TIMEOUT_MS = 5000;
const UNKNOWN_CHUNK_KEY = '__unknown_chunk__';

export interface ChunkResourceTiming {
  duration: number | null;
  transferSize: number | null;
  encodedBodySize: number | null;
  nextHopProtocol: string | null;
  responseStatus: number | null;
}

export interface ChunkConnectionInfo {
  effectiveType: string | null;
  rtt: number | null;
  saveData: boolean | null;
}

export interface ChunkFailureRecord {
  chunkUrl: string | null;
  attempt: number | null;
  msSincePageLoad: number | null;
  errorName: string;
  errorMessage: string;
  bootedEntry: string | null;
  online: boolean | null;
  connection: ChunkConnectionInfo | null;
  visibility: string | null;
  serviceWorker: boolean;
  resourceTiming: ChunkResourceTiming | null;
}

function safely<T>(read: () => T, fallback: T): T {
  try {
    const value = read();
    return value === undefined ? fallback : value;
  } catch (_e) {
    return fallback;
  }
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

function describeError(error: unknown): { name: string; message: string } {
  if (error && typeof error === 'object') {
    const { name, message } = error as { name?: unknown; message?: unknown };
    return {
      name: typeof name === 'string' ? name : 'UnknownError',
      message: typeof message === 'string' ? message : String(error),
    };
  }
  return { name: 'UnknownError', message: String(error) };
}

function findBootedEntry(): string | null {
  const script = safely(
    () =>
      document.querySelector<HTMLScriptElement>(ENTRY_SCRIPT_SELECTOR)?.src ||
      null,
    null,
  );
  if (script) return script;

  return safely(() => {
    const entry = performance
      .getEntriesByType('resource')
      .find((e) => ENTRY_URL_PATTERN.test(e.name));
    return entry ? entry.name : null;
  }, null);
}

function findConnection(): ChunkConnectionInfo | null {
  return safely(() => {
    const connection = (navigator as any).connection;
    if (!connection) return null;
    return {
      effectiveType: connection.effectiveType ?? null,
      rtt: connection.rtt ?? null,
      saveData: connection.saveData ?? null,
    };
  }, null);
}

function findResourceTiming(
  chunkUrl: string | null,
): ChunkResourceTiming | null {
  if (!chunkUrl) return null;

  return safely(() => {
    const entries = performance.getEntriesByName(chunkUrl);
    const entry = entries[entries.length - 1] as any;
    if (!entry) return null;
    return {
      duration: entry.duration ?? null,
      transferSize: entry.transferSize ?? null,
      encodedBodySize: entry.encodedBodySize ?? null,
      nextHopProtocol: entry.nextHopProtocol ?? null,
      responseStatus: entry.responseStatus ?? null,
    };
  }, null);
}

export function buildChunkFailureRecord(
  error: unknown,
  chunkUrl: string | null,
  attempt?: number,
): ChunkFailureRecord {
  const { name, message } = describeError(error);

  return {
    chunkUrl,
    attempt: attempt ?? null,
    msSincePageLoad: safely(() => Math.round(performance.now()), null),
    errorName: name,
    errorMessage: truncate(message, MAX_MESSAGE_LENGTH),
    bootedEntry: findBootedEntry(),
    online: safely(() => navigator.onLine, null),
    connection: findConnection(),
    visibility: safely(() => document.visibilityState, null),
    serviceWorker: safely(
      () => !!(navigator as any).serviceWorker?.controller,
      false,
    ),
    resourceTiming: findResourceTiming(chunkUrl),
  };
}

/**
 * Logs one structured, stringified record (picked up by Bugsnag as a
 * breadcrumb on any later event) and stores it in sessionStorage so it can be
 * re-logged after the recovery reload, which wipes in-memory breadcrumbs.
 */
export function recordChunkFailure(
  error: unknown,
  chunkUrl: string | null,
  attempt?: number,
): ChunkFailureRecord | null {
  try {
    const record = buildChunkFailureRecord(error, chunkUrl, attempt);
    const serialized = JSON.stringify(record);

    console.info(LOG_LABEL, serialized);

    try {
      sessionStorage.setItem(LAST_FAILURE_KEY, serialized);
    } catch (_e) {
      // sessionStorage may be unavailable or full; the log above still happened.
    }

    return record;
  } catch (_e) {
    return null;
  }
}

export interface ChunkCorsProbe {
  status?: number;
  contentType?: string | null;
  accessControlAllowOrigin?: string | null;
  age?: string | null;
  xCache?: string | null;
  via?: string | null;
  cacheControl?: string | null;
  ms: number;
  error?: string;
}

export interface ChunkNoCorsProbe {
  outcome: 'resolved' | 'rejected';
  error?: string;
}

export interface ChunkCachedProbe {
  status?: number;
  contentType?: string | null;
  contentLength?: string | null;
  ms: number;
  error?: string;
}

export interface ChunkProbeResults {
  cors?: ChunkCorsProbe;
  noCors?: ChunkNoCorsProbe;
  cached?: ChunkCachedProbe;
  revision?: string | null;
  liveEntry?: string | null;
}

type StoredFailure = ChunkFailureRecord & {
  probes?: ChunkProbeResults;
  notified?: boolean;
};

function errorString(error: unknown): string {
  const { name, message } = describeError(error);
  return `${name}: ${message}`;
}

function readStoredFailure(): StoredFailure | null {
  return safely(() => {
    const stored = sessionStorage.getItem(LAST_FAILURE_KEY);
    return stored ? (JSON.parse(stored) as StoredFailure) : null;
  }, null);
}

/** Updates the persisted record, but only if it still belongs to this chunk. */
function updateStoredFailure(
  chunkUrl: string | null,
  update: (stored: StoredFailure) => StoredFailure,
): void {
  try {
    const stored = readStoredFailure();
    if (!stored || stored.chunkUrl !== chunkUrl) return;
    sessionStorage.setItem(LAST_FAILURE_KEY, JSON.stringify(update(stored)));
  } catch (_e) {
    // Best effort.
  }
}

const memoryReportCounts: Record<string, number> = {};

function readReportCounts(): Record<string, number> {
  return safely(
    () => JSON.parse(sessionStorage.getItem(REPORT_COUNTS_KEY) || '{}'),
    {},
  );
}

function reportCount(key: string): number {
  return Math.max(memoryReportCounts[key] ?? 0, readReportCounts()[key] ?? 0);
}

function incrementReportCount(key: string): void {
  const next = reportCount(key) + 1;
  memoryReportCounts[key] = next;
  try {
    sessionStorage.setItem(
      REPORT_COUNTS_KEY,
      JSON.stringify({ ...readReportCounts(), [key]: next }),
    );
  } catch (_e) {
    // The in-memory count still limits reports for this page load.
  }
}

// Resets in-memory state between tests. Not for production use.
export function _resetChunkDiagnosticsForTesting(): void {
  Object.keys(memoryReportCounts).forEach(
    (key) => delete memoryReportCounts[key],
  );
}

async function withTimeout<T>(
  run: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    return await run(controller.signal);
  } finally {
    clearTimeout(timer);
  }
}

/** The same kind of request the failed import() made: a CORS GET. */
async function probeCors(chunkUrl: string): Promise<ChunkCorsProbe> {
  const started = Date.now();
  try {
    const response = await withTimeout((signal) =>
      fetch(chunkUrl, { mode: 'cors', cache: 'no-store', signal }),
    );
    const header = (name: string) => response.headers.get(name);
    // Only the status and headers matter; don't download the bundle.
    safely(() => void response.body?.cancel(), undefined);
    return {
      status: response.status,
      contentType: header('content-type'),
      accessControlAllowOrigin: header('access-control-allow-origin'),
      age: header('age'),
      xCache: header('x-cache'),
      via: header('via'),
      cacheControl: header('cache-control'),
      ms: Date.now() - started,
    };
  } catch (error) {
    return { error: errorString(error), ms: Date.now() - started };
  }
}

/**
 * Distinguishes CORS from network failures: an opaque no-cors response means
 * the request reached the edge even if the CORS probe above was rejected.
 */
async function probeNoCors(chunkUrl: string): Promise<ChunkNoCorsProbe> {
  try {
    await withTimeout((signal) =>
      fetch(chunkUrl, {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-store',
        signal,
      }),
    );
    return { outcome: 'resolved' };
  } catch (error) {
    return { outcome: 'rejected', error: errorString(error) };
  }
}

async function probeRevision(): Promise<string | null> {
  try {
    const response = await withTimeout((signal) =>
      fetch('/infra/health', { cache: 'no-store', signal }),
    );
    const body = await response.json();
    return typeof body?.revision === 'string' ? body.revision : null;
  } catch (_e) {
    return null;
  }
}

/** Entry bundle hash the server is handing out right now, from the page shell. */
async function probeLiveEntry(): Promise<string | null> {
  try {
    const response = await withTimeout((signal) =>
      fetch(window.location.href, {
        cache: 'no-store',
        headers: { Accept: 'text/html' },
        signal,
      }),
    );
    const html = await response.text();
    return html.match(ENTRY_URL_PATTERN)?.[1] ?? null;
  } catch (_e) {
    return null;
  }
}

/**
 * Looks at the copy of the chunk the browser has cached, which is what a
 * module import actually reads. The probes above use cache: 'no-store', so they
 * always see a healthy file even when the browser has a bad response (say a
 * proxy's 200 text/html placeholder) cached for the URL, which a plain reload
 * keeps re-serving. A content type that isn't JavaScript or CSS here is the
 * smoking gun.
 *
 * Call it before anything overwrites the cached copy. Stores the result on the
 * persisted record; never throws.
 */
export async function inspectCachedChunk(
  chunkUrl: string,
): Promise<ChunkCachedProbe | null> {
  try {
    if (!Bugsnag.isStarted()) return null;

    const started = Date.now();
    let cached: ChunkCachedProbe;
    try {
      const response = await withTimeout((signal) =>
        fetch(chunkUrl, { cache: 'force-cache', mode: 'cors', signal }),
      );
      safely(() => void response.body?.cancel(), undefined);
      cached = {
        status: response.status,
        contentType: response.headers.get('content-type'),
        contentLength: response.headers.get('content-length'),
        ms: Date.now() - started,
      };
    } catch (error) {
      cached = { error: errorString(error), ms: Date.now() - started };
    }

    updateStoredFailure(chunkUrl, (stored) => ({
      ...stored,
      probes: { ...stored.probes, cached },
    }));
    return cached;
  } catch (_e) {
    return null;
  }
}

function staleTab(
  record: ChunkFailureRecord,
  probes: ChunkProbeResults,
): boolean | null {
  const booted = record.bootedEntry?.match(ENTRY_URL_PATTERN)?.[1];
  const live = probes.liveEntry;
  return booted && live ? booted !== live : null;
}

function chunkFilename(chunkUrl: string | null): string {
  return chunkUrl?.split('?')[0].split('/').pop() || 'unknown-chunk';
}

function notifyChunkFailure(
  record: ChunkFailureRecord,
  probes: ChunkProbeResults,
  afterReload: boolean,
): void {
  const filename = chunkFilename(record.chunkUrl);
  const error = new Error(`Failed to load chunk ${filename}`);
  error.name = 'ChunkFetchFailure';

  Bugsnag.notify(
    error,
    (event) => {
      event.severity = 'warning';
      // Group per chunk filename, not per synthetic stack trace.
      event.groupingHash = `chunk-fetch-failure:${filename}`;
      event.addMetadata('chunk', {
        ...record,
        probes,
        staleTab: staleTab(record, probes),
        afterReload,
      });
    },
    () =>
      updateStoredFailure(record.chunkUrl, (stored) => ({
        ...stored,
        notified: true,
      })),
  );
}

/**
 * Probes the failed chunk and sends one Bugsnag report with the results.
 *
 * The recovery path reloads the page about a second after a failure, which
 * aborts in-flight requests. So each probe result is also written to the
 * persisted record as it arrives, and `logPreviousChunkFailure` sends the
 * report on the next page load if delivery did not complete first.
 *
 * Fire-and-forget: resolves once the report is handed to Bugsnag, never throws.
 */
export async function reportChunkFailure(
  record: ChunkFailureRecord,
): Promise<void> {
  try {
    if (!Bugsnag.isStarted()) return;

    const key = record.chunkUrl ?? UNKNOWN_CHUNK_KEY;
    if (reportCount(key) >= MAX_REPORTS_PER_CHUNK) return;
    incrementReportCount(key);

    const probes: ChunkProbeResults = {};
    const collect = async <K extends keyof ChunkProbeResults>(
      name: K,
      probe: Promise<ChunkProbeResults[K]>,
    ) => {
      probes[name] = await probe;
      updateStoredFailure(record.chunkUrl, (stored) => ({
        ...stored,
        probes: { ...stored.probes, [name]: probes[name] },
      }));
    };

    const chunkUrl = record.chunkUrl;
    await Promise.allSettled([
      chunkUrl ? collect('cors', probeCors(chunkUrl)) : undefined,
      chunkUrl ? collect('noCors', probeNoCors(chunkUrl)) : undefined,
      collect('revision', probeRevision()),
      collect('liveEntry', probeLiveEntry()),
    ]);

    // The cached-copy inspection runs from the recovery path, not from here.
    const stored = readStoredFailure();
    if (stored?.chunkUrl === record.chunkUrl && stored.probes?.cached) {
      probes.cached = stored.probes.cached;
    }

    notifyChunkFailure(record, probes, false);
  } catch (_e) {
    // Diagnostics must never surface an error.
  }
}

/**
 * Re-logs the failure recorded before the last reload, once, then clears it.
 * If that failure's report never finished delivering (the reload cut it off),
 * sends it now with whatever probe results were captured.
 */
export function logPreviousChunkFailure(): void {
  try {
    const stored = sessionStorage.getItem(LAST_FAILURE_KEY);
    if (!stored) return;

    sessionStorage.removeItem(LAST_FAILURE_KEY);

    // Only handle well-formed records; a corrupt value is dropped silently.
    const previous = JSON.parse(stored) as StoredFailure;
    console.info(`${LOG_LABEL} previous failure`, stored);

    if (previous.probes && !previous.notified && Bugsnag.isStarted()) {
      const { probes, notified: _notified, ...record } = previous;
      notifyChunkFailure(record, probes, true);
    }
  } catch (_e) {
    // Diagnostics must never break startup.
  }
}
